/// <amd-module name="Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaTable" />
import * as React from "react";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IObservableArray, IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { startsWith } from "VSS/Core/Util/String";
import { localeDateFormat } from "VSS/Platform/Date";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Dropdown, DropdownExpandableTextField } from "VSSUI/Dropdown";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem, ListBoxItemType, wrapListBoxItems } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { ITableColumn, SimpleTableCell, Table, TableCell } from "VSSUI/Table";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { FieldType } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/Common/WitSettingsViews/Resources";
import { WiqlOperators } from "Wit/Common/WiqlOperators";
import { DatePickerDialog } from "Wit/Common/WorkItemControls/DatePickerDialog";
import { FieldValue, WorkItemFieldControl } from "Wit/Common/WorkItemControls/WorkItemFieldControl";
import { getWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { getWorkItemFieldService as getWorkItemFieldService2} from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { isWorkItemIdentityRef } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { CriteriaConstants } from "./CriteriaConstants";
import { ICriteriaSettingData } from "./CriteriaInterfaces";

export interface CriteriaTableProps {
    /**
     * Optional class name
     */
    className?: string;
    /**
     * Determines if the table can be edited or if it is read only
     */
    disabled?: boolean;
    /**
     * Indicates if fields have been loaded yet
     */
    fieldsReady?: IObservableValue<boolean>;
    /**
     * Action to take when a row is added
     */
    onAddRow: () => void;
    /**
     * Action to take when a row is deleted
     */
    onDeleteRow: (rowIndex: number, item: ICriteriaSettingData) => void;
    /**
     * Action to take when a row's field is updated
     */
    onUpdateField: (rowIndex: number, item: ICriteriaSettingData, field: string) => void;
    /**
     * Action to take when a row's operator is updated
     */
    onUpdateOperator: (rowIndex: number, item: ICriteriaSettingData, operator: string) => void;
    /**
     * Action to take when a row's value is updated
     */
    onUpdateValue: (rowIndex: number, item: ICriteriaSettingData, value: string) => void;
    /**
     * Row data for all configured field criteria
     */
    rows: IObservableArray<ICriteriaSettingData>;

    /**
     * If false, the macro me picker provider will NOT be used for the identity field control.
     * Defaults to true
     */
    useMacroMePickerProvider?: boolean;
}

export class CriteriaTable extends TfsComponent<CriteriaTableProps> {
    public render(): JSX.Element {
        const { className, disabled, fieldsReady, rows } = this.props;
        return (
            <div className={className}>
                <Observer rows={rows}>
                    {() => (
                        <Table ariaLabel={Resources.FieldCriteriaHeader} columns={this._columns} itemProvider={rows} showHeader={rows.length > 0} />
                    )}
                </Observer>
                {!disabled && (
                    <div className="margin-top-8 padding-horizontal-20">
                        <Observer fieldsReady={fieldsReady} rows={rows}>
                            {({ fieldsReady, rows }) => (
                                <Button
                                    disabled={(fieldsReady !== undefined && !fieldsReady) || rows.length >= CriteriaConstants.maxCriteria}
                                    iconProps={{ iconName: "Add" }}
                                    onClick={() => this.props.onAddRow()}
                                    text={Resources.AddCriteriaButtonText}
                                />
                            )}
                        </Observer>
                    </div>
                )}
            </div>
        );
    }

    private _renderFieldColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ICriteriaSettingData>,
        tableItem: ICriteriaSettingData
    ): JSX.Element => {
        const onSelect = (event: any, item: IListBoxItem) => {
            this.props.onUpdateField(rowIndex, tableItem, item.id);
        };

        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                <Dropdown
                    className="wit-settings-table-dropdown"
                    disabled={this.props.disabled}
                    filterPlaceholderText={Resources.DropdownSearchPlaceholder}
                    items={tableItem.fields}
                    onSelect={onSelect}
                    selection={tableItem.fieldSelection}
                />
            </SimpleTableCell>
        );
    };

    private _renderOperatorColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ICriteriaSettingData>,
        tableItem: ICriteriaSettingData
    ): JSX.Element => {
        const onSelect = (event: any, item: IListBoxItem) => {
            this.props.onUpdateOperator(rowIndex, tableItem, item.id);
        };

        const dropdown = this._renderTableDropdown(tableItem.operators, onSelect, /** ready */ true, tableItem.operatorSelection);

        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                {dropdown}
            </SimpleTableCell>
        );
    };

    private _renderValueColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ICriteriaSettingData>,
        tableItem: ICriteriaSettingData
    ): JSX.Element => {
        const onChange = (value: FieldValue) => {
            const newValue = isWorkItemIdentityRef(value) ? value.distinctDisplayName : value?.toString() ?? "";
            this.props.onUpdateValue(rowIndex, tableItem, newValue);
        };

        const selection = new DropdownSelection();
        const date = new ObservableValue<string>("");
        const isDate = (item: ICriteriaSettingData) => {
            const fieldService  = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.UseNewWorkItemFieldService, false) 
               ? getWorkItemFieldService2(this.context.pageContext)
               : getWorkItemFieldService(this.context.pageContext);
            const field = fieldService.getFieldByReferenceName(item.field);
            return field?.type === FieldType.DateTime;
        };

        const onDatePickerChange = (item?: IListBoxItem<{}> | undefined) => {
            if (!item) {
                return;
            }

            if (item.id === "custom-date") {
                const layoutManager = this.context.pageContext!.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout(onDismiss => (
                    <DatePickerDialog
                        onDismiss={(value?) => {
                            // If no date was selected
                            if (!value) {
                                const prevSelectionIndex = tableItem.criteriaValue.value
                                    ? tableItem.criteriaValues.value.findIndex(listValue => tableItem.criteriaValue.value === listValue)
                                    : -1;
                                if (prevSelectionIndex !== -1) {
                                    // And previous selection, select previous item
                                    selection.select(prevSelectionIndex);
                                } else {
                                    // And no previous selection, clear selection.
                                    selection.clear();
                                }
                            } else {
                                this.props.onUpdateValue(rowIndex, tableItem, value.toLocaleDateString());
                                date.value = localeDateFormat(value, "d");
                            }

                            onDismiss();
                        }}
                    />
                ));
            } else {
                this.props.onUpdateValue(rowIndex, tableItem, item.text ?? "");
                date.value = item.text ?? "";
            }
        };

        if (isDate(tableItem)) {
            if (startsWith(tableItem.criteriaValue.value, WiqlOperators.MacroStart) || isNaN(Date.parse(tableItem.criteriaValue.value))) {
                date.value = tableItem.criteriaValue.value;
            } else {
                date.value = localeDateFormat(new Date(Date.parse(tableItem.criteriaValue.value)), "d");
            }
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                <FormItem className="full-width" error={tableItem.hasError}>
                    <Observer ready={tableItem.criteriaValues.ready}>
                        {({ ready }) => {
                            if (!ready) {
                                return <Spinner />;
                            } else if (isDate(tableItem)) {
                                const listOptions = [
                                    { id: "custom-date", text: Resources.CustomDate },
                                    { id: "custom-date-separator", type: ListBoxItemType.Divider },
                                    ...(wrapListBoxItems(tableItem.criteriaValues.value) ?? [])
                                ];
                                return (
                                    <EditableDropdown
                                        allowFreeform={true}
                                        allowTextSelection={true}
                                        allowClear={false}
                                        className="wit-settings-table-dropdown date-picker"
                                        disabled={this.props.disabled}
                                        items={listOptions}
                                        onValueChange={onDatePickerChange}
                                        renderExpandable={props => <DropdownExpandableTextField {...props} editable={true} />}
                                        selection={selection}
                                        selectedText={date}
                                    />
                                );
                            } else {
                                return (
                                    <WorkItemFieldControl
                                        allowedValues={tableItem.criteriaValues.value}
                                        className="wit-settings-table-dropdown"
                                        disabled={this.props.disabled}
                                        fieldName={tableItem.field}
                                        includeIterationMacros
                                        initialValue={tableItem.criteriaValue.value}
                                        onChange={onChange}
                                        useMacroMePickerProvider={this.props.useMacroMePickerProvider ?? true}
                                    />
                                );
                            }
                        }}
                    </Observer>
                </FormItem>
            </SimpleTableCell>
        );
    };

    private _renderDeleteColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ICriteriaSettingData>,
        tableItem: ICriteriaSettingData
    ): JSX.Element => {
        return (
            <TableCell className="bolt-table-cell-side-action" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="flex-column flex-center">
                    <Button
                        className="bolt-table-cell-content-reveal padding-horizontal-4"
                        disabled={this.props.disabled}
                        iconProps={{ iconName: "Delete" }}
                        onClick={() => this.props.onDeleteRow(rowIndex, tableItem)}
                        subtle
                    />
                </div>
            </TableCell>
        );
    };

    private _renderTableDropdown(
        items: IObservableArray<IListBoxItem>,
        onSelect: (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem) => void,
        ready: boolean,
        selection: DropdownSelection
    ): JSX.Element {
        return (
            <Dropdown
                className="wit-settings-table-dropdown"
                disabled={this.props.disabled}
                filterPlaceholderText={Resources.DropdownSearchPlaceholder}
                items={items}
                onSelect={onSelect}
                placeholder={!ready ? Resources.LoadingText : Resources.DropdownPlaceholder}
                selection={selection}
            />
        );
    }

    private _onSize = (event: MouseEvent, index: number, width: number) => {
        (this._columns[index].width as ObservableValue<number>).value = width;
    };

    private _columns: ITableColumn<ICriteriaSettingData>[] = [
        {
            id: "field",
            name: Resources.FieldColumnLabel,
            onSize: this._onSize,
            renderCell: this._renderFieldColumn,
            width: new ObservableValue(-33.34)
        },
        {
            id: "operator",
            name: Resources.OperatorColumnLabel,
            onSize: this._onSize,
            renderCell: this._renderOperatorColumn,
            width: new ObservableValue(-33.33)
        },
        {
            id: "value",
            name: Resources.ValueColumnLabel,
            onSize: this._onSize,
            renderCell: this._renderValueColumn,
            width: new ObservableValue(-33.33)
        },
        {
            id: "delete",
            onSize: this._onSize,
            renderCell: this._renderDeleteColumn,
            width: new ObservableValue(40)
        }
    ];
}
